import { useEffect, useRef, useState } from "react";
import { ApiError } from "@/api/client";
import { uploadAsset } from "@/api/assets";
import { useAvailableModelsQuery } from "@/api/settings";
import { NEW_PROJECT_KIND_KEY } from "@/components/template/NewMenu";

/** One-shot handoff read by the /create workspace's new-session seed effect.
 *  Holds the user's instruction + the uploaded reference image's id/filename.
 *  Consumed (removed) on the first mount, exactly like NEW_PROJECT_KIND_KEY.
 *
 *  The workspace seeds a normal SVG starter session and auto-dispatches this
 *  prompt with the image attached as a reference — the generation agent (via
 *  the backend's vectorize tool) owns mode/grid/colors, picking them from the
 *  user's language and the image itself. */
export const IMAGE_VECTOR_KEY = "motionforge-image-vector";

/** Used when the user leaves the instruction box empty. */
const DEFAULT_PROMPT =
  "Vectorize this image into a clean, editable SVG, faithful to the source.";

/** Accepted raster/animated inputs — matches the backend's reference-asset
 *  allow list (svg excluded; you can't vectorize a vector). */
const ACCEPT = "image/png,image/jpeg,image/webp,image/gif";

interface ImageToVectorDialogProps {
  open: boolean;
  onClose: () => void;
  /** Called after the image is uploaded and the handoff is stashed — the
   *  host routes into a fresh session exactly like its other kind choices
   *  (NewMenu → navigate("/create"); SessionSidebar → onNewSession()). */
  onConverted: () => void;
}

/** Pull a human-actionable message out of a failed upload. The backend
 *  returns JSON `{message}` on 400; fall back to the ApiError status line or a
 *  generic string so a failure is never swallowed. */
function uploadError(err: unknown): string {
  if (err instanceof ApiError) {
    const body = err.body as { message?: string; error?: string } | null;
    return body?.message ?? body?.error ?? err.message;
  }
  return err instanceof Error ? err.message : "Upload failed";
}

/**
 * Image → vector entry flow. Uploads the chosen image as a reference asset and
 * stashes a one-shot sessionStorage handoff (instruction + asset id) tagged
 * kind=svg, then hands control back to the host to open a fresh session. The
 * workspace seeds a normal SVG starter, attaches the image as a reference, and
 * auto-sends the instruction — the generation agent decides trace vs. pixel,
 * grid, and colors from the prompt and the image.
 *
 * Open-state is owned by the host (NewMenu / SessionSidebar) so a single dialog
 * instance can serve both entry points.
 */
export function ImageToVectorDialog({
  open,
  onClose,
  onConverted,
}: ImageToVectorDialogProps) {
  // Live availability: no Gemini image models = no restyle step server-side.
  const { data: availableModels } = useAvailableModelsQuery();
  const noImageModel = (availableModels?.image?.length ?? 0) === 0;

  const [file, setFile] = useState<File | null>(null);
  const [previewUrl, setPreviewUrl] = useState<string | null>(null);
  const [prompt, setPrompt] = useState("");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const contentRef = useRef<HTMLDivElement>(null);

  // Object URL for the preview thumbnail — revoked whenever it's replaced or
  // the component unmounts so we don't leak blob handles.
  useEffect(() => {
    if (!file) {
      setPreviewUrl(null);
      return;
    }
    const url = URL.createObjectURL(file);
    setPreviewUrl(url);
    return () => URL.revokeObjectURL(url);
  }, [file]);

  // Escape to close + lock body scroll while open (matches PreviewModal).
  useEffect(() => {
    if (!open) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape" && !busy) onClose();
    };
    window.addEventListener("keydown", onKey);
    const prevOverflow = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    return () => {
      window.removeEventListener("keydown", onKey);
      document.body.style.overflow = prevOverflow;
    };
  }, [open, onClose, busy]);

  // Reset transient state each time the dialog is opened afresh.
  useEffect(() => {
    if (open) return;
    setError(null);
    setBusy(false);
  }, [open]);

  if (!open) return null;

  const handleCreate = async () => {
    if (!file || busy) return;
    setBusy(true);
    setError(null);
    try {
      const asset = await uploadAsset(file, "reference");
      // Stash the instruction + uploaded image, tag the next session svg, then
      // hand off. The workspace's new-session seed reads and clears both keys,
      // seeds the SVG starter, and auto-dispatches the prompt with the image
      // attached as a reference.
      sessionStorage.setItem(
        IMAGE_VECTOR_KEY,
        JSON.stringify({
          prompt: prompt.trim() || DEFAULT_PROMPT,
          assetId: asset.id,
          filename: asset.filename ?? file.name,
        }),
      );
      sessionStorage.setItem(NEW_PROJECT_KIND_KEY, "svg");
      onClose();
      onConverted();
    } catch (err) {
      setError(uploadError(err));
    } finally {
      setBusy(false);
    }
  };

  return (
    <div
      role="dialog"
      aria-modal="true"
      aria-label="Convert image to vector"
      onMouseDown={(e) => {
        if (
          !busy &&
          contentRef.current &&
          !contentRef.current.contains(e.target as Node)
        ) {
          onClose();
        }
      }}
      className="fixed inset-0 z-50 flex items-center justify-center p-4"
      style={{ background: "rgba(0,0,0,0.6)" }}
    >
      <div
        ref={contentRef}
        className="flex max-h-[90vh] w-full max-w-md flex-col overflow-hidden rounded-2xl border border-line bg-surface shadow-2xl"
      >
        {/* Header */}
        <div className="flex items-center justify-between border-b border-line px-4 py-3">
          <h2 className="text-sm font-semibold text-ink">Image to vector</h2>
          <button
            type="button"
            onClick={onClose}
            disabled={busy}
            aria-label="Close"
            className="rounded-lg p-1 text-ink-3 transition-colors hover:bg-surface-3 hover:text-ink disabled:opacity-40"
          >
            <svg
              className="h-4 w-4"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              strokeWidth="2"
              aria-hidden
            >
              <path strokeLinecap="round" d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
        </div>

        {/* Body */}
        <div className="flex-1 space-y-4 overflow-y-auto px-4 py-4">
          {/* File input + preview */}
          <div>
            <label className="mb-1.5 block text-xs font-medium text-ink-2">
              Image
            </label>
            <div className="flex items-center gap-3">
              <div className="flex h-16 w-16 shrink-0 items-center justify-center overflow-hidden rounded-lg border border-line bg-surface-2">
                {previewUrl ? (
                  <img
                    src={previewUrl}
                    alt="Selected image preview"
                    className="h-full w-full object-contain"
                  />
                ) : (
                  <span className="text-[10px] text-ink-3">No image</span>
                )}
              </div>
              <div className="min-w-0 flex-1">
                <label className="inline-block cursor-pointer rounded-lg border border-line px-3 py-1.5 text-xs font-medium text-ink-2 transition-colors hover:bg-surface-3 hover:text-ink">
                  {file ? "Change image" : "Choose image"}
                  <input
                    type="file"
                    accept={ACCEPT}
                    className="hidden"
                    onChange={(e) => {
                      const f = e.target.files?.[0] ?? null;
                      if (f) {
                        setFile(f);
                        setError(null);
                      }
                      e.target.value = "";
                    }}
                  />
                </label>
                {file && (
                  <p className="mt-1 truncate text-xs text-ink-3" title={file.name}>
                    {file.name}
                  </p>
                )}
              </div>
            </div>
          </div>

          {/* Instruction */}
          <div>
            <label
              htmlFor="image-vector-prompt"
              className="mb-1.5 block text-xs font-medium text-ink-2"
            >
              What should we make?
            </label>
            <textarea
              id="image-vector-prompt"
              value={prompt}
              onChange={(e) => setPrompt(e.target.value)}
              rows={3}
              placeholder={
                'e.g. "Make a pixel art version of this character" or "Trace this into a clean vector"'
              }
              className="w-full resize-none rounded-lg border border-line bg-surface px-2.5 py-2 text-xs text-ink placeholder:text-ink-3 focus:border-line-strong focus:outline-none"
            />
            {noImageModel && (
              <p className="mt-1.5 rounded-lg border border-line bg-surface px-2.5 py-2 text-[11px] text-ink-3">
                No Gemini API key connected — stylized redraws (pixel art,
                style changes) will use the basic tracer and lose quality.
                Plain image→vector conversion is unaffected. Add a key under
                Settings → Model Providers → Google for best results.
              </p>
            )}
          </div>

          {error && <p className="text-xs text-danger">{error}</p>}
        </div>

        {/* Footer */}
        <div className="flex items-center justify-end gap-2 border-t border-line px-4 py-3">
          <button
            type="button"
            onClick={onClose}
            disabled={busy}
            className="rounded-lg px-3 py-1.5 text-xs font-medium text-ink-2 transition-colors hover:text-ink disabled:opacity-40"
          >
            Cancel
          </button>
          <button
            type="button"
            onClick={handleCreate}
            disabled={!file || busy}
            className="rounded-lg bg-accent px-4 py-1.5 text-xs font-medium text-accent-fg transition-colors hover:bg-accent-hover disabled:opacity-40"
          >
            {busy ? "Uploading…" : "Create"}
          </button>
        </div>
      </div>
    </div>
  );
}
